/* ll-cart-guard.js — flags basket lines this market cannot ship.
 *
 * The UK and US run parallel ranges (the UK twin carries a -uk handle) and
 * the basket SURVIVES a market switch, so a line can sit in the basket that
 * Shopify's checkout will refuse — previously with a live Checkout button and
 * no warning until the payment step.
 *
 * Liquid cannot see ordinary market exclusions: a product still resolves
 * completely in the cart. The route check blocks ordinary products only when
 * /products/{handle}.js returns 404. Explicit wall-art and digital products
 * retain their catalogue-policy gate because their sale rules are intentional.
 *
 * It lives in its own asset because the basket page is not the only place a
 * customer sees their lines: Dawn's cart drawer opens on every other page and
 * used to offer an unguarded Checkout. Progressive enhancement — with no JS
 * both surfaces behave exactly as they did before.
 *
 * Exposes window.LLCartGuard.check(root, { checkoutSelector }).
 *
 * Loaded twice on purpose — once from layout/theme.liquid for the drawer and
 * once from sections/ll-cart.liquid, so the basket page keeps its guard even
 * if only the basket closure has been deployed. The second copy must be inert.
 */
(function () {
  'use strict';

  if (window.LLCartGuard) return;

  /* the basket page marks its rows data-llb-handle; the drawer data-handle */
  var ITEM_SELECTOR = '[data-llb-item][data-llb-handle], .cart-item[data-handle]';
  var NAME_SELECTOR = '.llb-item__name, .cart-item__name';
  var OPEN_CLASS = /(^|\s)(active|is-open|open)(\s|$)/;
  /* The UK Star Book edition (-uk) also ships, with its PDF, to every other
     country that buys the UK edition (src/markets/editions.json). Only these
     handles: every other -uk product, and every wall-art line, keeps its own
     market rule. */
  var UK_STARBOOK = /^written-in-your-stars-(?:(?:sage-bookplate|oxblood)-)?uk$/;
  var UK_STARBOOK_COUNTRIES = ['GB', 'AU', 'DE', 'NZ', 'CA', 'SG'];
  var DIGITAL_STARBOOK = /^written-in-your-stars-(?:(?:sage-bookplate|oxblood)-)?digital$/;

  function all(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function handleOf(line) {
    return line.getAttribute('data-handle') || line.getAttribute('data-llb-handle') || '';
  }

  function countryOf(root) {
    var own = root.getAttribute && root.getAttribute('data-llb-country');
    if (own) return own;
    var holder = document.querySelector('[data-llb-country]');
    var name = holder && holder.getAttribute('data-llb-country');
    return name || 'your country';
  }

  function countryCodeOf(root) {
    var own = root.getAttribute && root.getAttribute('data-llb-country-code');
    if (own) return own.toUpperCase();
    var holder = document.querySelector('[data-llb-country-code]');
    return ((holder && holder.getAttribute('data-llb-country-code')) || '').toUpperCase();
  }

  function unavailableByCatalogue(line, countryCode) {
    var market = (line.getAttribute('data-availability') || '').toLowerCase();
    var explicitWallArt = line.getAttribute('data-explicit-wall-art') === 'true';
    var explicitDigital = line.getAttribute('data-explicit-digital') === 'true';
    /* For ordinary products the product.js route is authoritative. Keep a
       hard catalogue gate for the wall-art policy and digital launch state. */
    if (!explicitWallArt && !explicitDigital) return false;
    if (market === 'global') return false;
    if (market === 'gb' && UK_STARBOOK.test(handleOf(line))) return UK_STARBOOK_COUNTRIES.indexOf(countryCode) === -1;
    if (market === 'gb' || market === 'us') return market.toUpperCase() !== countryCode;
    return market === 'unknown';
  }

  function markLine(line, country) {
    line.classList.add('is-blocked');
    line.setAttribute('data-llb-blocked', '');
    var name = line.querySelector(NAME_SELECTOR);
    if (name && !line.querySelector('.llb-item__blocked')) {
      var note = document.createElement('p');
      note.className = 'llb-item__blocked';
      note.textContent = 'Not available in ' + country;
      name.insertAdjacentElement('afterend', note);
    }
    /* the product page is unreachable in this market, so "Edit" would only
       lead to a redirect — Remove is the action that helps */
    var edit = line.querySelector('[data-llb-edit]');
    if (edit) edit.remove();
  }

  function unmarkLine(line) {
    line.classList.remove('is-blocked');
    line.removeAttribute('data-llb-blocked');
    var note = line.querySelector('.llb-item__blocked');
    if (note) note.remove();
    line.removeAttribute('data-llb-digital-qty-invalid');
  }

  /* A digital Star Book is sold once per order. So is each line of a chart
     bundle (both lines carry a _Bundle property): the row marks it
     data-quantity-locked and the basket and drawer cap its input at 1. */
  function isDigitalStarbook(line) {
    return line.getAttribute('data-digital-starbook') === 'true' || DIGITAL_STARBOOK.test(handleOf(line));
  }

  function isQuantityLocked(line) {
    return isDigitalStarbook(line) || line.getAttribute('data-quantity-locked') === 'true';
  }

  /* A bundled chart is not a digital Star Book, so it must not be described
     as one — and neither may a mixed basket. */
  function quantityMessages(invalid) {
    var digital = invalid.filter(isDigitalStarbook).length;
    if (digital === invalid.length) {
      return {
        banner: 'A personalised digital Star Book can only be purchased once per order. Change its quantity to 1 to continue.',
        note: 'Change the digital Star Book quantity to 1 to continue.'
      };
    }
    if (digital === 0) {
      return {
        banner: 'Items in a bundle can only be purchased once per order. Change their quantity to 1 to continue.',
        note: 'Change the bundle item quantity to 1 to continue.'
      };
    }
    return {
      banner: 'Some items can only be purchased once per order. Change their quantity to 1 to continue.',
      note: 'Change the highlighted quantities to 1 to continue.'
    };
  }

  function markDigitalQuantity(line) {
    line.classList.add('is-blocked');
    line.setAttribute('data-llb-digital-qty-invalid', '');
    if (line.querySelector('.llb-item__digital-quantity')) return;
    var note = document.createElement('p');
    note.className = 'llb-item__blocked llb-item__digital-quantity';
    note.textContent = isDigitalStarbook(line)
      ? 'A personalised digital Star Book can only be purchased once per order. Change the quantity to 1 to continue.'
      : 'This item is part of a bundle and can only be purchased once per order. Change the quantity to 1 to continue.';
    var name = line.querySelector(NAME_SELECTOR);
    if (name) name.insertAdjacentElement('afterend', note); else line.appendChild(note);
  }

  function say(root, selector, message) {
    var el = root.querySelector(selector);
    if (!el) return;
    el.textContent = message;
    el.hidden = message === '';
  }

  function announce(root, blocked, country) {
    var plural = blocked.length !== 1;
    say(root, '[data-llb-blocked-banner]',
      (plural ? blocked.length + ' items aren’t' : 'One item isn’t')
      + ' available in ' + country + '. Remove ' + (plural ? 'them' : 'it')
      + ' to check out — or switch back to the country you were shopping in.');
    say(root, '[data-llb-blocked-note]',
      'Remove the item' + (plural ? 's' : '') + ' marked “Not available in '
      + country + '” to continue.');
  }

  /* Every Checkout control on the surface degrades together — the basket
     page renders one at the top and one in the summary, and leaving either
     live would only move the dead end further down. */
  function disableCheckout(root, selector) {
    all(root, selector).forEach(function (cta) {
      cta.classList.add('is-blocked');
      cta.setAttribute('aria-disabled', 'true');
      var href = cta.getAttribute('href');
      if (href) cta.setAttribute('data-llb-href', href);
      cta.removeAttribute('href');
      if (cta.tagName === 'BUTTON') cta.disabled = true;
    });
  }

  /* A blocked line can be removed, and Dawn re-renders the drawer around the
     lines that are left. A check that then finds nothing unshippable has to
     hand the customer their Checkout back — a stale warning is as bad a dead
     end as a missing one. */
  function restore(root, selector, itemSelector) {
    var stale = all(root, itemSelector).filter(function (line) {
      return line.hasAttribute('data-llb-blocked') || line.hasAttribute('data-llb-digital-qty-invalid');
    });
    var ctas = all(root, selector).filter(function (cta) {
      return cta.getAttribute('aria-disabled') === 'true';
    });
    if (!stale.length && !ctas.length) return; /* nothing to undo */

    stale.forEach(unmarkLine);
    ctas.forEach(function (cta) {
      cta.classList.remove('is-blocked');
      cta.removeAttribute('aria-disabled');
      var href = cta.getAttribute('data-llb-href');
      if (href) {
        cta.setAttribute('href', href);
        cta.removeAttribute('data-llb-href');
      }
      if (cta.tagName === 'BUTTON') cta.disabled = false;
    });
    say(root, '[data-llb-blocked-banner]', '');
    say(root, '[data-llb-blocked-note]', '');
  }

  function check(root, options) {
    var opts = options || {};
    if (!root || !root.querySelectorAll) return Promise.resolve([]);
    var checkoutSelector = opts.checkoutSelector || '[data-llb-checkout]';
    var itemSelector = opts.itemSelector || ITEM_SELECTOR;

    /* Each row is checked once. Marking them keeps a drawer re-render (which
       replaces the rows, clearing the mark) cheap and keeps the DOM writes
       below from re-triggering the observer that called us. */
    var lines = all(root, itemSelector).filter(function (line) {
      return !line.hasAttribute('data-llb-checked');
    });
    if (!lines.length) return Promise.resolve([]);
    lines.forEach(function (line) { line.setAttribute('data-llb-checked', ''); });

    /* Answers can arrive after the drawer has been re-rendered underneath
       them, pointing at rows that are no longer on the page. Only the newest
       run for this root may touch the page — an older one has been overtaken
       and its verdict is about a basket that no longer exists. The token is
       taken after the early return above, so a run that had nothing to do
       never invalidates one that did. */
    var token = (root.__llCartGuardRun || 0) + 1;
    root.__llCartGuardRun = token;

    var country = countryOf(root);
    var invalidQuantity = lines.filter(function (line) {
      if (!isQuantityLocked(line)) return false;
      var qty = line.querySelector('[data-llb-qty], .quantity__input');
      return !!qty && Number(qty.value) !== 1;
    });
    var countryCode = countryCodeOf(root);
    var catalogueBlocked = lines.filter(function (line) { return unavailableByCatalogue(line, countryCode); });
    var checks = lines.map(function (line) {
      var handle = handleOf(line);
      if (!handle) return Promise.resolve(null);
      return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        /* Only a 404 is evidence. A 500 or a transport failure says nothing
           about the catalogue — never block a line the customer may well be
           able to buy. */
        .then(function (response) { return response.status === 404 ? line : null; })
        .catch(function () { return null; });
    });

    return Promise.all(checks).then(function (results) {
      if (root.__llCartGuardRun !== token) return [];
      var blocked = Array.from(new Set(catalogueBlocked.concat(results.filter(Boolean))));
      if (!blocked.length && !invalidQuantity.length) {
        restore(root, checkoutSelector, itemSelector);
        return blocked;
      }
      blocked.forEach(function (line) { markLine(line, country); });
      invalidQuantity.forEach(markDigitalQuantity);
      if (blocked.length) announce(root, blocked, country);
      if (invalidQuantity.length) {
        var quantity = quantityMessages(invalidQuantity);
        say(root, '[data-llb-blocked-banner]', quantity.banner);
        say(root, '[data-llb-blocked-note]', quantity.note);
      }
      disableCheckout(root, checkoutSelector);
      return blocked.concat(invalidQuantity);
    });
  }

  function watchDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    var host = drawer.closest ? drawer.closest('cart-drawer') : null;
    var run = function () { check(drawer, { checkoutSelector: '[data-llb-drawer-checkout]' }); };
    var isOpen = function () { return !!host && OPEN_CLASS.test(host.className || ''); };

    if (typeof MutationObserver === 'function') {
      /* Dawn replaces the drawer's contents wholesale after every cart write */
      new MutationObserver(function () { if (isOpen()) run(); })
        .observe(drawer, { childList: true, subtree: true });
      if (host) {
        new MutationObserver(function () { if (isOpen()) run(); })
          .observe(host, { attributes: true, attributeFilter: ['class'] });
      }
    }
    if (isOpen()) run();
  }

  function start() {
    /* The local design harness serves no /products/*.js, where a 404 means
       "no such route", not "not sold here". Only a real storefront is
       evidence. */
    if (!window.Shopify || !window.fetch) return;
    var basket = document.getElementById('llb');
    if (basket) check(basket, { checkoutSelector: '[data-llb-checkout]' });
    watchDrawer();
  }

  window.LLCartGuard = { check: check };
  start();
})();
